"use strict";
var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b ||= {})
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
import React, { Component, createRef } from "react";
import { createPortal } from "react-dom";
import { Transition } from "react-transition-group";
import observeRect from "@reach/observe-rect";
import classNames from "classnames";
import ReactResizeDetector from "react-resize-detector/build/withPolyfill";
import { observer } from "mobx-react";
import { action, computed, observable } from "mobx";
import getOverlayPosition from "../../../utils/getOverlayPosition";
import getPortalRoot from "../../../utils/getPortalRoot";
import forceReflow from "../../../utils/forceReflow";
import { KeyType, randomString, shouldUpdatePosition } from "../../../utils";
import { AnimationDuration } from "../../../styles/Animations";
import Panel from "../Panel/Panel";
import { handleMouseUpByBlurring } from "../../../utils/focus";
import { Layer } from "../../../utils/layers/LayerContext";
import { scrollAwayObserver } from "../../../utils/scrollAwayObserver";
export var PopoverPosition = /* @__PURE__ */ ((PopoverPosition2) => {
  PopoverPosition2["Top"] = "top";
  PopoverPosition2["Right"] = "right";
  PopoverPosition2["Bottom"] = "bottom";
  PopoverPosition2["Left"] = "left";
  return PopoverPosition2;
})(PopoverPosition || {});
export var PopoverTheme = /* @__PURE__ */ ((PopoverTheme2) => {
  PopoverTheme2["Dark"] = "dark";
  PopoverTheme2["Light"] = "light";
  return PopoverTheme2;
})(PopoverTheme || {});
export var PopoverSize = /* @__PURE__ */ ((PopoverSize2) => {
  PopoverSize2["Small"] = "small";
  PopoverSize2["Medium"] = "medium";
  PopoverSize2["Large"] = "large";
  PopoverSize2["Auto"] = "auto";
  return PopoverSize2;
})(PopoverSize || {});
export var PopoverAlignment = /* @__PURE__ */ ((PopoverAlignment2) => {
  PopoverAlignment2["Start"] = "start";
  PopoverAlignment2["Center"] = "center";
  return PopoverAlignment2;
})(PopoverAlignment || {});
let Popover = class extends Component {
  constructor() {
    super(...arguments);
    this.internalIsOpen = false;
    this.keyboardTriggered = false;
    this.buttonRef = createRef();
    this.portalRoot = getPortalRoot("popover-root");
    this.randomId = randomString();
    this.setIsOpen = (isOpen, details) => {
      var _a, _b;
      (_b = (_a = this.props).onChange) == null ? void 0 : _b.call(_a, isOpen, details);
      this.internalIsOpen = isOpen;
    };
    this.handleButtonClick = (event) => {
      if (this.props.onClickStopPropagation) {
        event == null ? void 0 : event.stopPropagation();
      }
      this.setIsOpen(!this.isOpen, { reason: "clicked-button" });
    };
    this.handleButtonKeyDown = (event) => {
      if (event.key === KeyType.Enter) {
        this.keyboardTriggered = true;
      }
    };
    this.handleExited = () => {
      this.keyboardTriggered = false;
    };
  }
  get popoverId() {
    return this.props.id || `popover-${this.randomId}`;
  }
  get isOpen() {
    var _a;
    return (_a = this.props.isOpen) != null ? _a : this.internalIsOpen;
  }
  get caretSize() {
    return this.props.caret ? 14 : 0;
  }
  render() {
    const {
      button,
      children,
      size,
      className,
      theme,
      offset,
      position,
      recalculatePositionOnResize,
      padding,
      outerMargin
    } = this.props;
    return /* @__PURE__ */ React.createElement(React.Fragment, null, button({
      ref: this.buttonRef,
      ["aria-controls"]: this.popoverId,
      ["aria-expanded"]: this.isOpen,
      onClick: this.handleButtonClick,
      onKeyDown: this.handleButtonKeyDown
    }), /* @__PURE__ */ React.createElement(
      Transition,
      {
        mountOnEnter: true,
        unmountOnExit: true,
        in: this.isOpen,
        timeout: AnimationDuration.Medium,
        onExited: this.handleExited,
        onEnter: forceReflow
      },
      (status) => createPortal(
        /* @__PURE__ */ React.createElement(Layer, null, ({ zIndex }) => /* @__PURE__ */ React.createElement(
          PopoverPanel,
          {
            className,
            id: this.popoverId,
            theme,
            size,
            position,
            caretSize: this.caretSize,
            setIsOpen: this.setIsOpen,
            status,
            buttonRef: this.buttonRef,
            offset,
            keyboardTriggered: this.keyboardTriggered,
            maxHeight: this.props.maxHeight,
            style: { zIndex: this.props.autoStack ? zIndex : void 0 },
            recalculatePositionOnResize,
            alignment: this.props.alignment,
            padding,
            outerMargin
          },
          children
        )),
        this.portalRoot
      )
    ));
  }
};
Popover.defaultProps = {
  theme: "light" /* Light */,
  size: "medium" /* Medium */,
  position: "bottom" /* Bottom */,
  offset: 4,
  padding: "medium"
};
__decorateClass([
  observable.ref
], Popover.prototype, "internalIsOpen", 2);
__decorateClass([
  observable.ref
], Popover.prototype, "keyboardTriggered", 2);
__decorateClass([
  computed
], Popover.prototype, "popoverId", 1);
__decorateClass([
  computed
], Popover.prototype, "isOpen", 1);
__decorateClass([
  computed
], Popover.prototype, "caretSize", 1);
__decorateClass([
  action
], Popover.prototype, "setIsOpen", 2);
__decorateClass([
  action
], Popover.prototype, "handleButtonClick", 2);
__decorateClass([
  action
], Popover.prototype, "handleButtonKeyDown", 2);
__decorateClass([
  action
], Popover.prototype, "handleExited", 2);
Popover = __decorateClass([
  observer
], Popover);
let PopoverPanel = class extends Component {
  constructor() {
    super(...arguments);
    this.popoverRef = createRef();
    this.handleTargetRectChange = (targetRect) => {
      var _a;
      if (!targetRect || !shouldUpdatePosition(targetRect))
        return;
      const { caretSize, position, offset } = this.props;
      const overlayRect = (_a = this.popoverRef.current) == null ? void 0 : _a.getBoundingClientRect();
      const newPopoverPosition = getOverlayPosition({
        position,
        targetRect,
        overlayRect,
        offset: offset || 0 - caretSize + caretSize * Math.sqrt(2) / 2,
        caretSize,
        outerMargin: this.props.outerMargin,
        alignment: this.props.alignment
      });
      this.popoverPosition = newPopoverPosition;
    };
    this.handleOuterClick = (event) => {
      var _a, _b;
      const target = event.target;
      if (target !== this.popoverRef.current && target !== this.props.buttonRef.current && !((_a = this.popoverRef.current) == null ? void 0 : _a.contains(target)) && !((_b = this.props.buttonRef.current) == null ? void 0 : _b.contains(target)) && !(event.target instanceof HTMLElement && event.target.closest(".nested-portal"))) {
        this.props.setIsOpen(false, { reason: "clicked-outside" });
      }
    };
    this.handleKeyDown = (event) => {
      if (event.key === KeyType.Escape) {
        this.props.setIsOpen(false, { reason: "pressed-escape" });
      }
    };
    this.onResize = () => {
      if (this.props.recalculatePositionOnResize && this.props.buttonRef.current) {
        this.handleTargetRectChange(this.props.buttonRef.current.getBoundingClientRect());
      }
    };
  }
  componentDidMount() {
    var _a, _b;
    this.rectObserver = observeRect(this.props.buttonRef.current, this.handleTargetRectChange);
    (_a = this.rectObserver) == null ? void 0 : _a.observe();
    document.addEventListener("mousedown", this.handleOuterClick);
    document.addEventListener("keydown", this.handleKeyDown);
    if (this.props.buttonRef.current) {
      this.teardownScrollAway = scrollAwayObserver(this.props.buttonRef.current, {
        onScrollAway: () => {
          this.props.setIsOpen(false, { reason: "scrolled-away" });
        }
      });
    }
    if (this.props.keyboardTriggered) {
      (_b = this.popoverRef.current) == null ? void 0 : _b.focus();
    }
  }
  componentWillUnmount() {
    var _a, _b, _c;
    (_a = this.rectObserver) == null ? void 0 : _a.unobserve();
    document.removeEventListener("mousedown", this.handleOuterClick);
    document.removeEventListener("keydown", this.handleKeyDown);
    (_b = this.teardownScrollAway) == null ? void 0 : _b.call(this);
    if (this.props.keyboardTriggered) {
      (_c = this.props.buttonRef.current) == null ? void 0 : _c.focus();
    }
  }
  componentDidUpdate(prevProps) {
    var _a;
    if (this.props.buttonRef.current && (prevProps.size !== this.props.size || prevProps.caretSize !== this.props.caretSize || prevProps.offset !== this.props.offset)) {
      this.handleTargetRectChange((_a = this.props.buttonRef.current) == null ? void 0 : _a.getBoundingClientRect());
    }
  }
  get position() {
    var _a;
    return ((_a = this.popoverPosition) == null ? void 0 : _a.modifier) || this.props.position;
  }
  render() {
    var _a, _b, _c, _d;
    const { className, id, theme, size, caretSize, children, status, recalculatePositionOnResize, padding } = this.props;
    return /* @__PURE__ */ React.createElement(
      "div",
      {
        ref: this.popoverRef,
        className: classNames("popover", className),
        onClick: (event) => event.stopPropagation(),
        onMouseUp: handleMouseUpByBlurring,
        tabIndex: -1,
        id,
        style: __spreadProps(__spreadValues({}, (_a = this.props.style) != null ? _a : {}), {
          transform: `translate3d(${((_b = this.popoverPosition) == null ? void 0 : _b.overlay.left) || 0}px, ${((_c = this.popoverPosition) == null ? void 0 : _c.overlay.top) || 0}px, 0)`
        })
      },
      /* @__PURE__ */ React.createElement(
        Panel,
        {
          theme: theme === "dark" /* Dark */ ? "dark" : "light",
          className: classNames(
            "popover__panel",
            `popover__panel--${theme}`,
            `popover__panel--${status}`,
            `popover__panel--${size}`,
            `popover__panel--${this.position}`
          )
        },
        !!caretSize && /* @__PURE__ */ React.createElement(
          "div",
          {
            className: classNames("popover__caret", `popover__caret--${this.position}`, `popover__caret--${theme}`),
            style: __spreadProps(__spreadValues({}, (_d = this.popoverPosition) == null ? void 0 : _d.caret), {
              width: caretSize,
              height: caretSize
            })
          }
        ),
        /* @__PURE__ */ React.createElement("div", { className: "popover__panel-scroll", style: { maxHeight: this.props.maxHeight } }, /* @__PURE__ */ React.createElement("div", { className: classNames("popover__panel-content", `popover__panel-content--padding-${padding}`) }, children))
      ),
      recalculatePositionOnResize && /* @__PURE__ */ React.createElement(ReactResizeDetector, { onResize: this.onResize })
    );
  }
};
PopoverPanel.defaultProps = {
  outerMargin: 16
};
__decorateClass([
  observable.ref
], PopoverPanel.prototype, "popoverPosition", 2);
__decorateClass([
  computed
], PopoverPanel.prototype, "position", 1);
__decorateClass([
  action
], PopoverPanel.prototype, "handleTargetRectChange", 2);
__decorateClass([
  action
], PopoverPanel.prototype, "handleOuterClick", 2);
__decorateClass([
  action
], PopoverPanel.prototype, "handleKeyDown", 2);
PopoverPanel = __decorateClass([
  observer
], PopoverPanel);
export default Popover;
